import { useMemo, useState } from "react";
import { fetchAuthed } from "@/utils/api";
import type { SubmissionView, GameItem, VoteEntry } from "@/types/api";
import { Heart, ImageOff } from "lucide-react";

interface ActivityFeedProps {
  submissions: SubmissionView[];
  items: GameItem[];
  playerNames: Record<string, string>;
  myPlayerId: string;
  gameId: string;
  canInteract: boolean;
}

type FeedEvent =
  | { type: "approval"; submission: SubmissionView; timestamp: number }
  | { type: "heart"; submission: SubmissionView; vote: VoteEntry; timestamp: number };

function timeAgo(epochMs: number): string {
  const seconds = Math.floor((Date.now() - epochMs) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function ActivityFeed({
  submissions,
  items,
  playerNames,
  myPlayerId,
  gameId,
  canInteract,
}: ActivityFeedProps) {
  const [votingIds, setVotingIds] = useState<Set<string>>(new Set());
  const [optimisticVotes, setOptimisticVotes] = useState<Record<string, boolean>>({});

  const itemMap = useMemo(
    () => new Map(items.map((i) => [i.itemId, i])),
    [items],
  );

  const playerName = (pid: string) => playerNames[pid] ?? "Player";

  const events = useMemo(() => {
    const result: FeedEvent[] = [];
    for (const sub of submissions) {
      if (sub.moderationStatus !== "approved") continue;

      result.push({
        type: "approval",
        submission: sub,
        timestamp: sub.moderatedAt ?? sub.createdAt,
      });

      if (sub.votes) {
        for (const vote of sub.votes) {
          if (vote.votedAt === 0) continue; // skip legacy votes with no timestamp
          result.push({
            type: "heart",
            submission: sub,
            vote,
            timestamp: vote.votedAt,
          });
        }
      }
    }
    result.sort((a, b) => b.timestamp - a.timestamp);
    return result.slice(0, 50);
  }, [submissions]);

  const handleVote = async (sub: SubmissionView) => {
    const sid = sub.submissionId;
    setVotingIds((prev) => new Set(prev).add(sid));
    setOptimisticVotes((prev) => ({ ...prev, [sid]: true }));

    try {
      await fetchAuthed(gameId, `/games/${gameId}/submissions/${sid}/vote`, {
        method: "POST",
      });
    } catch {
      setOptimisticVotes((prev) => {
        const next = { ...prev };
        delete next[sid];
        return next;
      });
    } finally {
      setVotingIds((prev) => {
        const next = new Set(prev);
        next.delete(sid);
        return next;
      });
    }
  };

  const approved = submissions.filter((s) => s.moderationStatus === "approved");

  if (approved.length === 0) {
    return (
      <div className="flex flex-col items-center py-12 text-text-secondary">
        <ImageOff className="mb-3 h-10 w-10 opacity-40" />
        <p className="text-base font-medium">No activity yet</p>
        <p className="mt-1 text-sm">
          Photos will appear here as they&apos;re approved
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {events.map((event, idx) => {
        const sub = event.submission;
        const itemName = itemMap.get(sub.itemId)?.name ?? "an item";
        const isOwn = sub.playerId === myPlayerId;
        const hasVoted = sub.callerHasVoted || optimisticVotes[sub.submissionId];
        const canVote = canInteract && !isOwn && !hasVoted;

        if (event.type === "approval") {
          return (
            <div
              key={`a-${sub.submissionId}-${idx}`}
              className="overflow-hidden rounded-xl border border-border bg-surface"
            >
              <img
                src={sub.imageUrl}
                alt={itemName}
                className="w-full object-cover"
              />
              <div className="p-3">
                <p className="text-sm text-text">
                  <span className="font-semibold">{playerName(sub.playerId)}</span>
                  {isOwn && <span className="ml-1 text-xs text-primary">(you)</span>}
                  {" "}found{" "}
                  <span className="font-semibold">{itemName}</span>!
                </p>
                <div className="mt-1.5 flex items-center justify-between">
                  <p className="text-xs text-text-secondary">
                    {timeAgo(event.timestamp)}
                  </p>
                  {!isOwn ? (
                    <button
                      type="button"
                      onClick={() => canVote && handleVote(sub)}
                      disabled={!canVote || votingIds.has(sub.submissionId)}
                      className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-sm font-medium transition-colors ${
                        hasVoted
                          ? "bg-error/10 text-error"
                          : "bg-surface-sunken text-text-secondary hover:bg-error/10 hover:text-error"
                      } disabled:cursor-not-allowed`}
                    >
                      <Heart
                        className={`h-3.5 w-3.5 ${hasVoted ? "fill-current" : ""}`}
                      />
                      {sub.voteCount + (optimisticVotes[sub.submissionId] && !sub.callerHasVoted ? 1 : 0)}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm text-text-secondary">
                      <Heart className="h-3.5 w-3.5" />
                      {sub.voteCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        }

        // Heart event
        const voterName = playerName(event.vote.playerId);
        const isMyVote = event.vote.playerId === myPlayerId;
        return (
          <div
            key={`h-${sub.submissionId}-${event.vote.playerId}-${idx}`}
            className="flex items-center gap-3 rounded-xl border border-border/50 bg-surface/50 p-2"
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-error/10">
              <Heart className="h-4 w-4 fill-error text-error" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-text">
                <span className="font-semibold">{voterName}</span>
                {isMyVote && <span className="ml-1 text-xs text-primary">(you)</span>}
                {" "}loved{" "}
                <span className="font-semibold">{playerName(sub.playerId)}</span>
                &apos;s photo of{" "}
                <span className="font-semibold">{itemName}</span>
              </p>
              <p className="text-xs text-text-secondary">
                {timeAgo(event.timestamp)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
